<template>
  <div
    id="select-branch"
    :style="cssVars"
    :class="{ standalone }" >
    <h5>{{ title }}</h5>
    <div
      v-if="listBranches.length > 5"
      class="search-input-wrapper">
      <b-form-input
        ref="branchSearchBar"
        v-model="branchSearchValue"
        type="search"
        placeholder="Search numbers"/>
      <SBIcon
        v-if="branchSearchValue"
        :icon="SBIconList.CLOSE_THIN"
        :color="SBColors.blackUltralight"
        :hover-icon-color="SBColors.blackPrimary"
        :size="8"
        :hover-size="16"
        :pointer="true"
        @click="clearBranchSearchValue" />
    </div>
    <ul
      ref="branchesWrapperRef"
      class="branch-list">
      <li
        v-for="(branch, index) in listBranches"
        :key="index"
        class="flex flex-align-center"
        :class="{selected: (selectedId === index)}"
        @click="selectBranch(branch)"
        @mouseover="mouseSelect(index)"
        @mouseout="deselect()" >

        <div class="branch-icon-wrapper">
          <VNodes :vnodes="getNumberIcon(branch)"/>
        </div>
        <div class="option-info greedy">
          <h3
            class="ellipsis"
            :inner-html.prop="branch.name" />
          <p
            class="ellipsis"
            :inner-html.prop="branch.phone | phoneFormat" />
        </div>
        <SBIcon
          v-if="selectedBranch && selectedBranch.id && selectedBranch.id === branch.id"
          :icon="SBIconList.DONE_TICK"
          :color="SBColors.bluePrimary"
          :size="9" />
      </li>
      <span
        v-if="listBranches.length === 0"
        class="branch-not-found" >There is no match for '<b>{{ branchSearchValue }}</b>'.</span>
    </ul>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import { getNumberIcon } from '@/helpers/lists/branchIcons';
import VNodes from '@/components/common/VNodes';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

/**
 * This is copied to src/components/organisation/assign_conversation/AssignMenu.vue
 * Components should be refactored and combined into one generic search+select component with slots and props...
 */

export default {
  name: 'SelectBranch',
  components: {
    SBIcon,
    VNodes,
  },
  mixins: [
    UserPermissionsMixin,
  ],
  props: {
    onlyAvailable: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: 'Select branch',
    },
    standalone: {
      type: Boolean,
      required: false,
      default: false,
    },
    top: {
      type: Number,
      required: false,
      default: 0,
    },
    right: {
      type: Number,
      required: false,
      default: 0,
    },
    selectedBranch: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      branchSearchValue: '',
      SBIconList,
      SBColors,
      selectedId: null,
      searchUsed: true, // Set this to false if we decide to enable arrow keys only after the search has been used.
      mouseSelectAllowed: true,
      mouseResetTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      organisationNumbers: 'organisation/organisationNumbers',
    }),
    cssVars() {
      return {
        '--top': `${this.top}px`,
        '--right': `${this.right}px`,
      };
    },
    listBranches() {
      if (!this.onlyAvailable) {
        return this.organisationNumbers.filter(number => number.name.toLowerCase().includes(this.branchSearchValue.toLowerCase()) || number.phone.includes(this.branchSearchValue));
      }
      return this.currentOrganisation.businesses.filter(business => business.name.toLowerCase().includes(this.branchSearchValue.toLowerCase()) || business.phone.includes(this.branchSearchValue));
    },
  },
  watch: {
    branchSearchValue() {
      this.searchUsed = true;
      this.selectedId = 0;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    if (this.$refs.branchSearchBar) {
      this.$refs.branchSearchBar.focus();
    }
    if (this.$refs.branchesWrapperRef) {
      // Setting a min height on numbers list so when we search the list doesn't shrink and break.
      this.$refs.branchesWrapperRef.style.minHeight = `${this.$refs.branchesWrapperRef.offsetHeight}px`;
    }
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    getNumberIcon,
    clearBranchSearchValue() {
      this.branchSearchValue = '';
    },
    selectBranch(branch) {
      this.$emit('branch-selected', branch);
    },
    mouseSelect(itemId) {
      if (this.mouseSelectAllowed) {
        this.select(itemId);
      }
    },
    select(itemId) {
      this.selectedId = itemId;
      if (this.$refs.branchesWrapperRef && this.$refs.branchesWrapperRef.children) {
        this.scrollIntoViewIfNeeded(this.$refs.branchesWrapperRef.children[itemId]);
      }
    },
    deselect() {
      if (this.mouseSelectAllowed) {
        this.selectedId = null;
      }
    },
    scrollIntoViewIfNeeded(target) {
      const rectParent = target.parentElement.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      this.$nextTick(() => {
        const pseudoElementStyle = getComputedStyle(target.parentElement);
        const borderTopWidth = Number.parseInt(pseudoElementStyle.borderTopWidth.replace('px', ''), 10);
        const topPadding = Number.parseInt(pseudoElementStyle.paddingTop.replace('px', ''), 10);
        const parentTop = rectParent.top + borderTopWidth + topPadding;
        const parentBottom = rectParent.bottom + borderTopWidth + topPadding;
        if (rect.top < parentTop) {
          target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
        } else if (rect.bottom > parentBottom) {
          target.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
        }
      });
    },
    /**
     * Sets flag to not react on mouse events on the li items. If this is off and mouse is above a li item and we initiate changes with the arrow keys
     * then at some point the list will scroll and the mouse event will be trigered which will cause unwanted result.
     */
    preventMouseAction() {
      this.mouseSelectAllowed = false;
      clearTimeout(this.mouseResetTimeout);
      this.mouseResetTimeout = setTimeout(() => {
        this.mouseSelectAllowed = true;
      }, 1000);
    },
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 37: // LEFT
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId > 0) {
            this.select(this.selectedId - 1);
          }
          event.preventDefault();
          break;
        case 38: // UP
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId > 0) {
            this.select(this.selectedId - 1);
          }
          event.preventDefault();
          break;
        case 39: // RIGHT
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId === null) {
            this.select(0);
          } else if (this.searchUsed && this.selectedId < this.listBranches.length - 1) {
            this.select(this.selectedId + 1);
          }
          event.preventDefault();
          break;
        case 40: // DOWN
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId === null) {
            this.select(0);
          } else if (this.searchUsed && this.selectedId < this.listBranches.length - 1) {
            this.select(this.selectedId + 1);
          }
          event.preventDefault();
          break;
        case 13: // ENTER
          if (this.selectedId >= 0) {
            this.selectBranch(this.listBranches[this.selectedId]);
          }
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#select-branch {
  width: 297px;

  &.standalone {
    position: absolute;
    top: var(--top);
    right: var(--right);
    background: $white;
  }

  h5 {
    text-align: center;
    font-weight: 500;
    margin: 15px 16px 0;
  }

  .search-input-wrapper {
    position: relative;
    // border-bottom: solid 1px #e0e7e9;

    input {
      padding: 5px 26px 5px 8px;
      margin: 15px 16px 0;
      width: calc(100% - 30px);
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.15) !important;
      border: solid 1px #cfd8dc !important;
      background-color: #ffffff !important;
      height: 26px !important;

      &:focus {
        box-shadow: 0 0 0 3px #bfdeff !important;
      }
    }

    .sb-icon {
      position: absolute;
      top: 5px;
      right: 20px;
    }
  }

  .branch-list {
    margin: 0;
    margin-top: 15px;
    padding: 7px 0;
    border-radius: 0 0 5px 5px;
    border-top: solid 1px #e0e7e9;
    overflow: hidden;
    max-height: 232px;
    overflow-y: auto;

    li {
      padding: 8px 16px;
      .branch-icon {
        display: inline-block;
      }

      &.selected {
        background: $hover;
        cursor: pointer;
      }
    }

    .branch-not-found {
      display: block;
      padding: 0 16px;
    }
  }

  .branch-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 32px;
    border-radius: 5px;
    border: solid 1px #dde6e9;
    background-color: $white;

    .branch-icon {
      font-size: 14px !important;
      // letter-spacing: 3px !important;
    }

    .FRONTAPP,
    .FRONTPACHA {
      background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
      background-size: 14px 14px;
      background-position: center center;
      background-repeat: no-repeat;
      display: block;
      width: 31px;
      height: 31px;
      transform: translateY(0);
    }
  }

  .option-info {
    margin-left: 8px;
    padding-right: 10px;
    max-width: calc(100% - 42px);

    h3 {
      font-size: 14px;
      font-weight: 500;
      color: $blackPrimary;
    }

    p {
      font-size: 12px;
      font-weight: 400;
      color: $greyPrimary;
      margin: 3px 0 0 0 !important;
    }
  }
}

.popover-body {
  p {
    font-size: 14px;
    line-height: 1.2;
    color: $blackPrimary;
    margin: 1px 0 0 0;
    padding: 0;
  }
}
</style>
